import { Box, Button, Grid, Header, Input, Select, SpaceBetween, Spinner } from "@cloudscape-design/components"
import { OptionDefinition } from "@cloudscape-design/components/internal/components/option/interfaces";
import { useEffect, useMemo, useState } from "react";
import { ExtensionObjectType } from "types/extensionTypes";
import ExtensionCard from "./ExtensionCard";
import deploymentService from "services/deploymentService";
import { useCluster } from "contexts/ClusterContext";
import { useNotification } from "contexts/NotificationContext";
import DeleteConfirmationModal from "components/shared/DeleteConfirmationModal";




function InstalledExtensionsTab({ refreshKey }: { refreshKey: number }) {
    const [installedExtensionsList, setInstalledExtensionsList] = useState<ExtensionObjectType[]>([]);
    const [loadingInstalledExtensions, setLoadingInstalledExtensions] = useState(false);
    const [errorLoadingInstalledExtensions, setErrorLoadingInstalledExtensions] = useState<string | null>(null);

    const [extensionSearch, setExtensionSearch] = useState("");
    const [selectedCategory, setSelectedCategory] = useState<OptionDefinition>({ label: "All Categories", value: "all" });
    const [categoriesList, setCategoriesList] = useState<OptionDefinition[]>([]);

    const { selectedCluster } = useCluster();

    const { addNotification } = useNotification();

    const [loadingUninstallation, setLoadingUninstallation] = useState(false);

    const [showUninstallModal, setShowUninstallModal] = useState(false);

    const [selectedExtension, setSelectedExtension] = useState<ExtensionObjectType | null>(null);



    const fetchInstalledExtensions = async ()=>{
      try {
        setLoadingInstalledExtensions(true);
        const response = await deploymentService.getAllExtensions(selectedCluster?.value, "community", "installed,uninstalling"); 

        setInstalledExtensionsList(response);           
      
        const categories = Array.from(
          new Set<string>(
            response.map(
              (ext: ExtensionObjectType) => ext.metadata.category
            )
          )
        );

        const extensionCategoriesList: OptionDefinition[] = [
          { label: "All Categories", value: "all" },
          ...categories.map((category) => ({
            label: category,
            value: category,
          })),
        ];

        setCategoriesList(extensionCategoriesList);
      
      } catch (error) {
        setErrorLoadingInstalledExtensions("Failed to load installed extensions");
        console.error("Failed to fetch installed extensions", error);
      } finally{
        setLoadingInstalledExtensions(false);
      }
    }

    useEffect(()=>{
      fetchInstalledExtensions();
    },[selectedCluster?.value, refreshKey])



    const uninstallExtension = async(extension: ExtensionObjectType)=>{
      setLoadingUninstallation(true);
      try {
        
        await deploymentService.uninstallExtension(selectedCluster?.value, extension.id);
        
        fetchInstalledExtensions();
        addNotification("success", `${extension.displayName} uninstallation has started.`);

      } catch (error) {
        console.error(`Failed to Uninstall ${extension.displayName}`, error);
        addNotification("error", `Failed to uninstall ${extension.displayName}.`);

      } finally{
        setLoadingUninstallation(false);
      }
    }

    const openUninstallModal = (extension: ExtensionObjectType)=>{
      setSelectedExtension(extension);
      setShowUninstallModal(true);
    }



    // Derived state: Automatically re-calculates whenever search OR category changes
    const filteredExtensions = useMemo(() => {
      return installedExtensionsList.filter((extension) => {
        // 1. Matches Search?
        const matchesSearch = extension.displayName
          .toLowerCase()
          .includes(extensionSearch.trim().toLowerCase());
  
        // 2. Matches Category?
        const matchesCategory =
          selectedCategory.value === "all" ||
          extension.metadata.category === selectedCategory.value;
  
        // Must match BOTH conditions
        return matchesSearch && matchesCategory;
      });
    }, [installedExtensionsList, extensionSearch, selectedCategory]);


    // 1. LOADING STATE
    if (loadingInstalledExtensions) {
      return (
        <Box textAlign="center" padding={{ vertical: "xxl" }}>
          <SpaceBetween size="m">
            <Spinner size="large" />
            <Box color="text-body-secondary">Loading installed extensions...</Box>
          </SpaceBetween>
        </Box>
      );
    }

    // 2. ERROR STATE
    if (errorLoadingInstalledExtensions) {
      return (
        <Box textAlign="center" padding={{ vertical: "xxl" }}>
          <SpaceBetween size="m">
            <Box variant="h3" color="text-status-error">{errorLoadingInstalledExtensions}</Box>
            <Button onClick={fetchInstalledExtensions} iconName="refresh">Retry</Button>
          </SpaceBetween>
        </Box>
      );
    }

    // 3. EMPTY STATE
    if (installedExtensionsList.length === 0) {
      return (
      <Box textAlign="center" padding={{ vertical: "xxl" }}>
        <SpaceBetween size="m">
          <Box variant="h3">No extensions installed.</Box>
          <Box color="text-body-secondary">
            Install extensions from the Extensions tab. 
          </Box>
        </SpaceBetween>
      </Box>
        );
    }


    return (
    <SpaceBetween size="xl">
      <SpaceBetween size="s" direction="horizontal">
        <Input 
          value={extensionSearch} 
          onChange={({detail})=>setExtensionSearch(detail.value)} 
          type="search" 
          placeholder="Search extensions..." 
          className="extensions-search-input"
        />

        <Select
          selectedOption={selectedCategory}
          onChange={({ detail }) => setSelectedCategory(detail.selectedOption)}
          options={categoriesList}
        />
      </SpaceBetween>

      <SpaceBetween size="xl">
        <div className="header-container">
          <Header variant="h3">
            Installed Extensions
          </Header>
        </div>

        {filteredExtensions.length === 0 ?
        (
          <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
            <SpaceBetween size="m">
              <div>
                <b>No extensions found</b>
                <Box variant="p" color="text-body-secondary">
                  We couldn't find any extensions matching your search or filters. Try adjusting your search term or clearing selected categories.
                </Box>
              </div>
              <Button onClick={() => { setExtensionSearch(""); setSelectedCategory(categoriesList[0]); }}>
                Clear filters
              </Button>
            </SpaceBetween>
          </Box>
        ):(
          <Grid
            gridDefinition={filteredExtensions.map(()=>({ colspan: { default: 12, s: 4, m: 3, l: 3 } }))}
          >
            {filteredExtensions.map((extension, index)=> 
              <ExtensionCard 
                key={index} 
                viewMode="installed-tab" 
                extension={extension} 
                onUninstall={openUninstallModal}
              />)}    
          </Grid>
        )}
      </SpaceBetween>

      <DeleteConfirmationModal 
        isVisible={showUninstallModal} 
        isDeleting={loadingUninstallation} 
        onDismiss={()=>{
          setSelectedExtension(null);
          setShowUninstallModal(false);
        }} 
        onConfirm={async ()=>{
          if(!selectedExtension) return;

          await uninstallExtension(selectedExtension);

          setShowUninstallModal(false);
          setSelectedExtension(null);
        }} 
        deleteConfirmationValue={"uninstall extension"} 
        deleteAction = "uninstallation"
        headerText = "Uninstall Extension"
        headerIconName = "status-stopped"
        mainButtonText = "Uninstall"
        mainButtonLoadingText = "Uninstalling..."
      >
        Are you sure you want to uninstall extension: <b>{selectedExtension?.displayName}</b> from <b>{selectedCluster?.label}</b> ?  
      </DeleteConfirmationModal>
    </SpaceBetween>
  )
}

export default InstalledExtensionsTab
